import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Button, Flex, VStack, Text } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';

import LoadingScreen from './LoadingScreen';
import ErrorScreen from './ErrorScreen';
import IntroScreen from './IntroScreen';
import ChoiceButton from './ChoiceButton';
import ProgressBar from './ProgressBar';
import CompletionScreen from './CompletionScreen';
import MatrixQuestion from './MatrixQuestion';
import MultiSelectQuestion from './MultiSelectQuestion';
import LikertScale from './LikertScale';
import { surveyTheme as theme } from './surveyTheme';
import { t } from './translations';

const MotionBox = motion(Box);

// ============================================================================
// SEEDED PRNG (mulberry32) — deterministic randomization per respondent
// ============================================================================

function mulberry32(seed) {
  let s = seed | 0;
  return function () {
    s = (s + 0x6D2B79F5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle(array, rng) {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// ============================================================================
// RANDOMIZATION + ROTATION
// ============================================================================

function applyRandomization(surveyData, seed) {
  if (seed == null) return;

  const rng = mulberry32(seed);
  const rotationGroupPermutations = {};

  const processItems = (items) => {
    for (const item of items) {
      if (item.item_type !== 'question' || !item.question_data) continue;
      const q = item.question_data;
      if (!q.randomize_choices) continue;

      const anchoredTop = q.choices.filter(c => c.anchor_position === 'top');
      const anchoredBottom = q.choices.filter(c => c.anchor_position === 'bottom');
      const randomizable = q.choices.filter(c => !c.anchor_position || c.anchor_position === 'none');

      let shuffled;
      if (q.rotation_group && rotationGroupPermutations[q.rotation_group]) {
        // Reuse the permutation from the first question in this rotation group
        const existingOrder = rotationGroupPermutations[q.rotation_group];
        shuffled = reorderByIdSequence(randomizable, existingOrder);
      } else {
        shuffled = seededShuffle(randomizable, rng);
        if (q.rotation_group) {
          rotationGroupPermutations[q.rotation_group] = shuffled.map(c => c.id);
        }
      }

      q.choices = [...anchoredTop, ...shuffled, ...anchoredBottom];
    }
  };

  if (surveyData.survey_items) {
    processItems(surveyData.survey_items);
  } else if (surveyData.questions) {
    processItems(surveyData.questions.map(q => ({ item_type: 'question', question_data: q })));
  }
}

function reorderByIdSequence(choices, idOrder) {
  const idMap = Object.fromEntries(choices.map(c => [c.id, c]));
  const ordered = [];
  // Place choices that appear in the existing order first
  for (const id of idOrder) {
    if (idMap[id]) {
      ordered.push(idMap[id]);
      delete idMap[id];
    }
  }
  // Append any remaining choices not in the original order
  ordered.push(...Object.values(idMap));
  return ordered;
}

// ============================================================================
// SKIP LOGIC EVALUATION
// ============================================================================

function evaluateSkipLogic(question, allResponses) {
  if (!question.skip_logic || !question.skip_logic.show_if) return true;

  const { show_if } = question.skip_logic;
  const conditions = show_if.conditions || [show_if];
  const logic = show_if.logic || 'and';

  const results = conditions.map(condition => {
    const { question_id, operator, choice_ids } = condition;
    const response = allResponses[question_id];

    switch (operator) {
      case 'includes_any': {
        if (!response) return false;
        if (response.choice_id) return choice_ids.includes(parseInt(response.choice_id));
        if (response.choice_ids) return response.choice_ids.some(id => choice_ids.includes(parseInt(id)));
        return false;
      }
      case 'includes_all': {
        if (!response || !response.choice_ids) return false;
        return choice_ids.every(id => response.choice_ids.includes(String(id)));
      }
      case 'excludes_all': {
        if (!response) return true;
        if (response.choice_id) return !choice_ids.includes(parseInt(response.choice_id));
        if (response.choice_ids) return !response.choice_ids.some(id => choice_ids.includes(parseInt(id)));
        return true;
      }
      case 'answered':
        return response !== undefined;
      case 'not_answered':
        return response === undefined;
      default:
        return true;
    }
  });

  return logic === 'or' ? results.some(Boolean) : results.every(Boolean);
}

// ============================================================================
// HELPERS
// ============================================================================

const parseIntoPages = (surveyItems) => {
  if (!surveyItems || surveyItems.length === 0) return [];
  const pages = [];
  let currentPage = [];
  surveyItems.forEach((item) => {
    if (item.item_type === 'page_break') {
      if (currentPage.length > 0) {
        pages.push(currentPage);
        currentPage = [];
      }
    } else {
      currentPage.push(item);
    }
  });
  if (currentPage.length > 0) pages.push(currentPage);
  return pages;
};

const questionsToPages = (questions) => {
  if (!questions || questions.length === 0) return [];
  return questions.map((q) => [{ item_type: 'question', question_data: q }]);
};

const countQuestions = (pages) => {
  return pages.reduce((count, page) => {
    return count + page.filter(item => item.item_type === 'question').length;
  }, 0);
};

const getPageQuestions = (page) => {
  return page.filter(item => item.item_type === 'question');
};

function isQuestionAnswered(question, response) {
  if (!response) return false;
  const qType = question.question_type || 'single_choice';
  switch (qType) {
    case 'matrix':
      if (!question.matrix_rows || question.matrix_rows.length === 0) return true;
      if (!response.matrix_responses) return false;
      return question.matrix_rows.every(row => response.matrix_responses[row.id]?.choice_id);
    case 'multi_select':
      return response.choice_ids && response.choice_ids.length > 0;
    case 'single_choice':
    case 'likert':
    default:
      return response.choice_id !== undefined;
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const SurveyExperience = ({ previewMode = false, pollId: propPollId }) => {
  const { token } = useParams();

  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentScreen, setCurrentScreen] = useState('intro');
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [responses, setResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [lang, setLang] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('lang') === 'es' ? 'es' : 'en';
  });

  const localized = useCallback((en, es) => (lang === 'es' && es) ? es : en, [lang]);

  const submittedResponses = useRef(new Set());

  // Parse survey data into pages
  const pages = useMemo(() => {
    if (!surveyData) return [];
    if (surveyData.survey_items && surveyData.survey_items.length > 0) {
      return parseIntoPages(surveyData.survey_items);
    }
    if (surveyData.questions && surveyData.questions.length > 0) {
      return questionsToPages(surveyData.questions);
    }
    return [];
  }, [surveyData]);

  const totalQuestions = useMemo(() => countQuestions(pages), [pages]);

  // Fetch survey data
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const url = previewMode
          ? `/api/polls/${propPollId}/preview/`
          : `/survey/survey-data/${token}/`;

        const options = previewMode ? { credentials: 'include' } : {};
        const response = await fetch(url, options);

        if (response.status === 410) {
          const data = await response.json();
          setError({ type: 'expired', message: data.error || 'This survey link has expired' });
          setLoading(false);
          return;
        }

        if (!response.ok) {
          const data = await response.json();
          setError({ type: 'invalid', message: data.error || 'Invalid survey link' });
          setLoading(false);
          return;
        }

        const data = await response.json();

        // Check if survey was already completed
        if (data.already_completed) {
          setError({ type: 'already_completed', message: 'You have already completed this survey. Thank you for your participation.' });
          setLoading(false);
          return;
        }

        // Set language from API if not already set via URL param
        const urlParams = new URLSearchParams(window.location.search);
        if (!urlParams.get('lang') && data.preferred_language === 'es') {
          setLang('es');
        }

        // Apply randomization before setting state
        applyRandomization(data, data.randomization_seed);

        setSurveyData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching survey:', err);
        setError({ type: 'network', message: 'Unable to load survey. Please check your connection.' });
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [token, previewMode, propPollId]);

  // Build submission payload for all question types
  const submitPageResponses = useCallback(async (visibleQuestions, isLastPage) => {
    if (previewMode) return { success: true };

    const responsesToSubmit = [];

    for (const item of visibleQuestions) {
      const questionId = item.question_data.id;
      const response = responses[questionId];
      if (!response) continue;

      const qType = item.question_data.question_type || 'single_choice';

      if (qType === 'single_choice' || qType === 'likert') {
        const key = `${questionId}-${response.choice_id}`;
        if (submittedResponses.current.has(key)) continue;
        responsesToSubmit.push({
          question_id: parseInt(questionId, 10),
          choice_id: parseInt(response.choice_id, 10),
          timestamp: response.timestamp,
        });
      } else if (qType === 'multi_select') {
        const sortedIds = [...(response.choice_ids || [])].sort().join(',');
        const key = `${questionId}-multi-${sortedIds}`;
        if (submittedResponses.current.has(key)) continue;
        for (const choiceId of (response.choice_ids || [])) {
          responsesToSubmit.push({
            question_id: parseInt(questionId, 10),
            choice_id: parseInt(choiceId, 10),
            timestamp: response.timestamps?.[choiceId] || new Date().toISOString(),
          });
        }
      } else if (qType === 'matrix') {
        for (const [rowId, rowResponse] of Object.entries(response.matrix_responses || {})) {
          const key = `${questionId}-${rowId}-${rowResponse.choice_id}`;
          if (submittedResponses.current.has(key)) continue;
          responsesToSubmit.push({
            question_id: parseInt(questionId, 10),
            choice_id: parseInt(rowResponse.choice_id, 10),
            matrix_row_id: parseInt(rowId, 10),
            timestamp: rowResponse.timestamp,
          });
        }
      }
    }

    if (responsesToSubmit.length === 0 && !isLastPage) {
      return { success: true };
    }

    const payload = { responses: responsesToSubmit, mark_complete: isLastPage };

    try {
      const response = await fetch(`/survey/submit-response/${token}/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.status === 410) {
        const data = await response.json();
        return { success: false, error: { type: 'expired', message: data.error } };
      }
      if (response.status === 429) {
        const data = await response.json();
        return { success: false, error: { type: 'rate_limit', message: data.error } };
      }
      if (!response.ok) {
        const data = await response.json();
        return { success: false, error: { type: 'submit_error', message: data.error || 'Failed to submit' } };
      }

      // Mark as submitted
      for (const item of visibleQuestions) {
        const questionId = item.question_data.id;
        const resp = responses[questionId];
        if (!resp) continue;
        const qType = item.question_data.question_type || 'single_choice';
        if (qType === 'single_choice' || qType === 'likert') {
          submittedResponses.current.add(`${questionId}-${resp.choice_id}`);
        } else if (qType === 'multi_select') {
          const sortedIds = [...(resp.choice_ids || [])].sort().join(',');
          submittedResponses.current.add(`${questionId}-multi-${sortedIds}`);
        } else if (qType === 'matrix') {
          for (const [rowId, rowResp] of Object.entries(resp.matrix_responses || {})) {
            submittedResponses.current.add(`${questionId}-${rowId}-${rowResp.choice_id}`);
          }
        }
      }

      return { success: true };
    } catch (err) {
      console.error('[Survey] Fetch error:', err.name, err.message, err);
      return { success: false, error: { type: 'network', message: `Unable to submit: ${err.message}` } };
    }
  }, [token, responses, previewMode]);

  // Single choice / likert selection
  const handleChoiceSelect = useCallback((questionId, choiceId) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: {
        choice_id: choiceId,
        timestamp: new Date().toISOString(),
      },
    }));
  }, []);

  // Multi-select toggle
  const handleMultiSelectToggle = useCallback((questionId, choiceId, maxSelections) => {
    setResponses((prev) => {
      const existing = prev[questionId] || { choice_ids: [], timestamps: {} };
      const choiceStr = String(choiceId);
      const isSelected = existing.choice_ids?.includes(choiceStr);

      if (isSelected) {
        const newIds = existing.choice_ids.filter(id => id !== choiceStr);
        const newTimestamps = { ...existing.timestamps };
        delete newTimestamps[choiceStr];
        return { ...prev, [questionId]: { choice_ids: newIds, timestamps: newTimestamps } };
      }

      if (maxSelections && existing.choice_ids?.length >= maxSelections) {
        return prev; // At limit, don't add
      }

      return {
        ...prev,
        [questionId]: {
          choice_ids: [...(existing.choice_ids || []), choiceStr],
          timestamps: { ...(existing.timestamps || {}), [choiceStr]: new Date().toISOString() },
        },
      };
    });
  }, []);

  // Matrix row selection
  const handleMatrixSelect = useCallback((questionId, rowId, choiceId) => {
    setResponses((prev) => {
      const existing = prev[questionId] || { matrix_responses: {} };
      return {
        ...prev,
        [questionId]: {
          matrix_responses: {
            ...existing.matrix_responses,
            [rowId]: {
              choice_id: String(choiceId),
              timestamp: new Date().toISOString(),
            },
          },
        },
      };
    });
  }, []);

  // Navigation
  const handleNext = useCallback(async () => {
    if (pages.length === 0) return;

    const currentPage = pages[currentPageIndex];
    const pageQuestions = getPageQuestions(currentPage);
    const visibleQuestions = pageQuestions.filter(
      item => evaluateSkipLogic(item.question_data, responses)
    );
    const isLastPage = currentPageIndex === pages.length - 1;

    setSubmitting(true);
    const result = await submitPageResponses(visibleQuestions, isLastPage);

    if (!result.success) {
      setError(result.error);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);

    if (isLastPage) {
      setCurrentScreen('complete');
    } else {
      // Auto-advance past pages where all questions are skipped
      let nextIndex = currentPageIndex + 1;
      while (nextIndex < pages.length - 1) {
        const nextPage = pages[nextIndex];
        const nextQuestions = getPageQuestions(nextPage);
        const nextVisible = nextQuestions.filter(
          item => evaluateSkipLogic(item.question_data, responses)
        );
        if (nextVisible.length > 0 || nextQuestions.length === 0) break;
        nextIndex++;
      }
      setCurrentPageIndex(nextIndex);
    }
  }, [pages, currentPageIndex, submitPageResponses, responses]);

  const handleBack = useCallback(() => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex((prev) => prev - 1);
    }
  }, [currentPageIndex]);

  const handleStart = useCallback(() => {
    setCurrentScreen('questions');
  }, []);

  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    window.location.reload();
  }, []);

  // Current page state with skip logic
  const currentPage = pages[currentPageIndex];
  const pageQuestions = currentPage ? getPageQuestions(currentPage) : [];
  const visibleQuestions = pageQuestions.filter(
    item => evaluateSkipLogic(item.question_data, responses)
  );
  const allQuestionsAnswered = visibleQuestions.every(
    item => isQuestionAnswered(item.question_data, responses[item.question_data.id])
  );
  const isLastPage = currentPageIndex === pages.length - 1;

  // Progress calculation
  const questionsAnsweredSoFar = useMemo(() => {
    let count = 0;
    for (let i = 0; i < currentPageIndex; i++) {
      count += getPageQuestions(pages[i]).length;
    }
    if (currentPage) {
      count += visibleQuestions.filter(
        item => isQuestionAnswered(item.question_data, responses[item.question_data.id])
      ).length;
    }
    return count;
  }, [pages, currentPageIndex, currentPage, visibleQuestions, responses]);

  // Loading / Error / Intro / Complete screens
  if (loading) return <LoadingScreen />;
  if (error) return <ErrorScreen error={error} onRetry={handleRetry} lang={lang} />;

  if (currentScreen === 'intro') {
    return (
      <>
        {previewMode && <PreviewBanner />}
        <IntroScreen
          title={surveyData.title}
          titleEs={surveyData.title_es}
          prologue={surveyData.prologue}
          prologueEs={surveyData.prologue_es}
          questionCount={totalQuestions}
          onStart={handleStart}
          lang={lang}
          setLang={setLang}
        />
      </>
    );
  }

  if (currentScreen === 'complete') {
    return (
      <>
        {previewMode && <PreviewBanner />}
        <CompletionScreen previewMode={previewMode} lang={lang} />
      </>
    );
  }

  // Questions screen
  return (
    <Box minH="100vh" bg={theme.colors.background} display="flex" flexDirection="column">
      {previewMode && <PreviewBanner />}

      {/* Progress bar */}
      <Box position="fixed" top={previewMode ? '40px' : 0} left={0} right={0} bg={theme.colors.background} zIndex={10}>
        <ProgressBar current={questionsAnsweredSoFar} total={totalQuestions} />
      </Box>

      {/* Page content */}
      <Box flex={1} pt={previewMode ? 12 : 6} pb={32} overflowY="auto">
        <AnimatePresence mode="wait">
          <MotionBox
            key={currentPageIndex}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            width="100%"
            maxW="560px"
            mx="auto"
            px={theme.spacing.screenPadding}
          >
            <VStack spacing={8} align="stretch">
              {currentPage && currentPage.map((item, index) => {
                if (item.item_type === 'text_block') {
                  return (
                    <Box key={`text-${index}`}>
                      <Text
                        fontSize="16px"
                        lineHeight="1.6"
                        color={theme.colors.textPrimary}
                        fontFamily={theme.typography.fontFamily}
                        whiteSpace="pre-wrap"
                      >
                        {localized(item.text_content, item.text_content_es)}
                      </Text>
                    </Box>
                  );
                }

                if (item.item_type === 'question' && item.question_data) {
                  const question = item.question_data;

                  // Skip logic: hide question if condition not met
                  if (!evaluateSkipLogic(question, responses)) {
                    return null;
                  }

                  const qType = question.question_type || 'single_choice';

                  return (
                    <Box key={`question-${question.id}`}>
                      <VStack spacing={theme.spacing.questionGap} align="stretch">
                        {/* Question header */}
                        <Box>
                          {visibleQuestions.length === 1 && (
                            <Text
                              fontSize={theme.typography.progress.fontSize}
                              fontWeight={theme.typography.progress.fontWeight}
                              letterSpacing={theme.typography.progress.letterSpacing}
                              color={theme.colors.textMuted}
                              mb={3}
                              fontFamily={theme.typography.fontFamily}
                            >
                              {t(lang, 'pageOf', { current: currentPageIndex + 1, total: pages.length })}
                            </Text>
                          )}
                          <Text
                            as="h2"
                            fontSize={theme.typography.question.fontSize}
                            fontWeight={theme.typography.question.fontWeight}
                            lineHeight={theme.typography.question.lineHeight}
                            color={theme.colors.textPrimary}
                            fontFamily={theme.typography.fontFamily}
                          >
                            {localized(question.text, question.text_es)}
                          </Text>
                        </Box>

                        {/* Question body by type */}
                        {qType === 'matrix' ? (
                          <MatrixQuestion
                            question={question}
                            responses={responses[question.id]?.matrix_responses || {}}
                            onSelect={(rowId, choiceId) => handleMatrixSelect(question.id, rowId, choiceId)}
                            lang={lang}
                          />
                        ) : qType === 'multi_select' ? (
                          <MultiSelectQuestion
                            question={question}
                            selectedIds={responses[question.id]?.choice_ids || []}
                            onToggle={(choiceId) => handleMultiSelectToggle(question.id, choiceId, question.max_selections)}
                            maxSelections={question.max_selections}
                            lang={lang}
                          />
                        ) : qType === 'likert' ? (
                          <LikertScale
                            question={question}
                            selectedChoiceId={responses[question.id]?.choice_id || ''}
                            onSelect={(choiceId) => handleChoiceSelect(question.id, String(choiceId))}
                            lang={lang}
                          />
                        ) : (
                          /* single_choice (default) */
                          <VStack spacing={theme.spacing.choiceGap} align="stretch" role="radiogroup">
                            {question.choices.map((choice) => (
                              <ChoiceButton
                                key={choice.id}
                                choice={choice}
                                isSelected={(responses[question.id]?.choice_id || '') === String(choice.id)}
                                onSelect={(id) => handleChoiceSelect(question.id, String(id))}
                                lang={lang}
                              />
                            ))}
                          </VStack>
                        )}
                      </VStack>
                    </Box>
                  );
                }

                return null;
              })}

              {/* Page indicator for multi-question pages */}
              {visibleQuestions.length > 1 && (
                <Text
                  fontSize="13px"
                  color={theme.colors.textMuted}
                  textAlign="center"
                  fontFamily={theme.typography.fontFamily}
                >
                  {t(lang, 'pageOf', { current: currentPageIndex + 1, total: pages.length })}
                </Text>
              )}
            </VStack>
          </MotionBox>
        </AnimatePresence>
      </Box>

      {/* Navigation */}
      <Box
        position="fixed"
        bottom={0}
        left={0}
        right={0}
        bg={theme.colors.background}
        borderTop="1px solid"
        borderColor={theme.colors.borderLight}
        px={theme.spacing.screenPadding}
        py={4}
        pb={{ base: 8, md: 4 }}
      >
        <Flex maxW="560px" mx="auto" gap={3}>
          {currentPageIndex > 0 && (
            <Button
              onClick={handleBack}
              variant="ghost"
              size="lg"
              height="56px"
              px={6}
              fontSize="16px"
              fontWeight={500}
              fontFamily={theme.typography.fontFamily}
              borderRadius={theme.radii.button}
              color={theme.colors.textMuted}
              _hover={{ bg: theme.colors.borderLight }}
            >
              {t(lang, 'back')}
            </Button>
          )}

          <Button
            onClick={handleNext}
            isDisabled={!allQuestionsAnswered || submitting}
            isLoading={submitting}
            loadingText={t(lang, 'saving')}
            flex={1}
            size="lg"
            height="56px"
            fontSize="17px"
            fontWeight={600}
            fontFamily={theme.typography.fontFamily}
            borderRadius={theme.radii.button}
            bg={allQuestionsAnswered ? theme.colors.teal : theme.colors.borderLight}
            color={allQuestionsAnswered ? 'white' : theme.colors.textMuted}
            _hover={allQuestionsAnswered ? { bg: theme.colors.tealLight } : {}}
            _active={allQuestionsAnswered ? { transform: 'scale(0.99)' } : {}}
            _disabled={{
              bg: theme.colors.borderLight,
              color: theme.colors.textMuted,
              cursor: 'not-allowed',
              opacity: 1,
            }}
            transition={`background-color ${theme.transitions.normal}`}
          >
            {isLastPage ? t(lang, 'submit') : t(lang, 'next')}
          </Button>
        </Flex>
      </Box>
    </Box>
  );
};

const PreviewBanner = () => (
  <Box
    position="fixed"
    top={0}
    left={0}
    right={0}
    bg="purple.600"
    color="white"
    py={2}
    px={4}
    textAlign="center"
    fontSize="14px"
    fontWeight="600"
    fontFamily={theme.typography.fontFamily}
    zIndex={20}
  >
    PREVIEW MODE - This is exactly how respondents will see the survey
  </Box>
);

export default SurveyExperience;
